import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Send, X, Loader2, MessageSquare, Database, Sparkles } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import ChatMessage, { formatMessage } from './ChatMessage';
import { lireFluxSse, estUnAbandon } from '../utils/lireFluxSse';

const API_URL = config.API_URL;

/**
 * ChatPanel - Panneau latéral de conversation avec Claude
 * Supporte le streaming en temps réel pour l'analyse initiale
 */
const ChatPanel = ({
  isOpen,
  onClose,
  analysisResult,
  accountId,
  accountName,
  token,
  initialQuery,
  // Props pour le streaming initial
  streamingMode = false,
  libraryItemIds = [],
  onStreamComplete,
  /**
   * Analyse d'un dashboard affiché : le corps à envoyer à `/api/analyze-dashboard/stream`.
   *
   * Distinct de `streamingMode`, qui fait refaire au serveur sa propre collecte à partir
   * d'identifiants de comptes. Ici l'analyse porte sur les chiffres **déjà à l'écran**, qui ne
   * tiendraient pas dans une URL : d'où un POST lu comme un flux, et non un `EventSource`.
   */
  dashboardStream = null,
  /**
   * Panneau posé à côté du contenu plutôt que par-dessus.
   *
   * Sans voile, le dashboard reste lisible et manipulable pendant qu'on lit l'analyse — c'est
   * tout l'intérêt d'un panneau latéral sur une modale.
   */
  sansOverlay = false
}) => {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  // États pour le streaming
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingPhase, setStreamingPhase] = useState('');
  const [streamingData, setStreamingData] = useState(null);
  const eventSourceRef = useRef(null);
  // La requête en vol, pour l'interrompre si l'on referme le panneau en cours d'analyse.
  const abortRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll vers le bas quand de nouveaux messages arrivent
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus sur l'input quand le panel s'ouvre
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Démarrer le streaming quand le panel s'ouvre en mode streaming
  useEffect(() => {
    if (isOpen && streamingMode && libraryItemIds.length > 0 && initialQuery && !isStreaming && messages.length === 0) {
      startStreaming();
    }

    // Cleanup: fermer EventSource quand le composant est démonté
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [isOpen, streamingMode, libraryItemIds, initialQuery]);

  /*
   * Le panneau s'ouvre vide et attend une question.
   *
   * Il lançait l'analyse de lui-même à l'ouverture. C'était un appel facturé à chaque clic, pour
   * un texte générique que personne n'avait demandé — et qui n'était presque jamais la question
   * que l'on se posait en ouvrant le panneau. La première question de l'utilisateur fait office
   * d'analyse initiale : elle part sur la même route, avec les mêmes chiffres en contexte.
   *
   * Cet effet ne sert donc plus qu'à interrompre une analyse en vol quand le panneau se ferme.
   */
  useEffect(() => {
    if (!isOpen) {
      abortRef.current?.abort();
      return undefined;
    }

    // Une fermeture d'onglet ne doit pas laisser une analyse courir pour rien.
    return () => abortRef.current?.abort();
  }, [isOpen]);

  // Créer une conversation normale (non-streaming) quand le panel s'ouvre
  useEffect(() => {
    if (isOpen && !streamingMode && !conversation && accountId && initialQuery && analysisResult) {
      createConversation();
    }
  }, [isOpen, streamingMode, accountId, initialQuery, analysisResult]);

  /**
   * Démarre le streaming SSE pour l'analyse initiale
   */
  /*
   * La largeur du panneau, tirée à la souris.
   *
   * **Une seule variable à écrire.** `--largeur-panneau-analyse` commande déjà la largeur du
   * panneau, le retrait du dashboard et le décalage de la bulle d'aide : il suffit de la
   * changer pour que les trois suivent. C'est précisément ce pour quoi elle avait été posée.
   *
   * **Les transitions sont coupées pendant le geste.** Le contenu glisse sur 0,3 s, ce qui est
   * juste quand le panneau s'ouvre, et insupportable quand on le tire — la page traîne derrière
   * le curseur. Une classe sur `body` les neutralise le temps du déplacement.
   *
   * **La largeur est retenue.** Quelqu'un qui élargit le panneau le fait pour lire, pas pour
   * cette question-là ; la lui redemander à chaque ouverture serait la même inattention que de
   * rouvrir une barre latérale qu'il avait repliée.
   */
  const MEMOIRE_LARGEUR = 'kokpit_largeur_panneau_analyse';
  const LARGEUR_MIN = 360;

  /**
   * La largeur maximale se déduit de ce qu'il faut laisser au dashboard, pas d'un pourcentage.
   *
   * Un plafond à 70 % de la fenêtre paraissait raisonnable ; mesuré, il laissait le tableau
   * déborder de 46 px sous le panneau. La mise en page a une largeur minimale propre — barre
   * repliée, marges, colonnes — en deçà de laquelle elle cesse de se comprimer et passe sous le
   * panneau au lieu de rétrécir.
   *
   * On réserve donc 640 px au contenu. Le pourcentage reste en second plafond, pour qu'un très
   * grand écran ne se retrouve pas avec un panneau démesuré.
   */
  const PLACE_MINIMALE_DU_CONTENU = 640;
  const largeurMax = () => Math.max(
    LARGEUR_MIN,
    Math.min(window.innerWidth - PLACE_MINIMALE_DU_CONTENU, Math.round(window.innerWidth * 0.7))
  );

  const poserLargeur = useCallback((px) => {
    const borne = Math.min(Math.max(px, LARGEUR_MIN), largeurMax());
    document.documentElement.style.setProperty('--largeur-panneau-analyse', `${borne}px`);
    return borne;
  }, []);

  useEffect(() => {
    if (!isOpen || !sansOverlay) return undefined;

    const retenue = Number(window.localStorage.getItem(MEMOIRE_LARGEUR));
    if (retenue > 0) poserLargeur(retenue);

    // La largeur survit à la fermeture : elle est un réglage, pas un état de la conversation.
    return undefined;
  }, [isOpen, sansOverlay, poserLargeur]);

  /*
   * Le geste est capturé par la poignée, et non écouté sur le document.
   *
   * Avec des écouteurs posés sur `document`, un relâchement hors de la fenêtre — ou intercepté
   * par un autre élément — n'arrive jamais : les écouteurs restent accrochés et le panneau
   * continue de suivre le curseur après que l'utilisateur a lâché. `setPointerCapture` évite
   * cela par construction, en dirigeant tous les événements du pointeur vers la poignée jusqu'à
   * sa libération, quoi qu'il survole.
   *
   * `pointercancel` est traité comme un relâchement : le système peut reprendre le pointeur —
   * un geste de défilement, une fenêtre qui prend le focus — et l'état de redimensionnement ne
   * doit pas lui survivre.
   */
  const commencerRedimensionnement = (evenement) => {
    evenement.preventDefault();
    const poignee = evenement.currentTarget;
    poignee.setPointerCapture(evenement.pointerId);
    document.body.classList.add('redimensionnement-en-cours');

    // La largeur se déduit du bord droit de la fenêtre : le panneau y est collé.
    const deplacer = (e) => poserLargeur(window.innerWidth - e.clientX);

    const terminer = (e) => {
      const finale = poserLargeur(window.innerWidth - e.clientX);
      window.localStorage.setItem(MEMOIRE_LARGEUR, String(finale));
      document.body.classList.remove('redimensionnement-en-cours');
      poignee.removeEventListener('pointermove', deplacer);
      poignee.removeEventListener('pointerup', terminer);
      poignee.removeEventListener('pointercancel', terminer);
      if (poignee.hasPointerCapture(e.pointerId)) poignee.releasePointerCapture(e.pointerId);
    };

    poignee.addEventListener('pointermove', deplacer);
    poignee.addEventListener('pointerup', terminer);
    poignee.addEventListener('pointercancel', terminer);
  };

  /**
   * Analyse du dashboard affiché, écrite au fil de l'eau.
   *
   * La mécanique du flux — le POST, le refus en code HTTP, le dernier fragment retenu — vit dans
   * `utils/lireFluxSse.js`, partagée avec la génération de dashboards sur mesure. Ne reste ici que
   * ce que les évènements **veulent dire** pour ce panneau.
   *
   * @param {string} [question] La question posée. Elle part en `userBrief` : le serveur en fait
   *   le titre de la conversation *et* la consigne donnée au modèle, si bien que la première
   *   question tient lieu d'analyse initiale. Sans elle, le serveur retombe sur sa formulation
   *   générique — c'est le chemin que suivent les autres dashboards.
   */
  const startDashboardStream = async (question) => {
    setIsStreaming(true);
    setStreamingContent('');
    setStreamingPhase('Lecture des résultats…');
    setError(null);

    const controleur = new AbortController();
    abortRef.current = controleur;

    /*
     * La charge se lit **ici**, au moment de l'envoi.
     *
     * Le dashboard charge ses composants en deux vagues, la seconde en arrière-plan : une charge
     * figée à l'ouverture du panneau capturait un écran à moitié rempli. Le parent passe donc
     * une fonction, qu'on appelle quand la question part. Un objet reste accepté — les autres
     * dashboards n'ont pas ce besoin.
     */
    const corps = typeof dashboardStream === 'function' ? dashboardStream() : dashboardStream;

    /*
     * ⚠️ `texte` et `idConversation` vivent **hors** du gestionnaire d'évènements, pas dedans :
     * `setStreamingContent` est asynchrone, et le texte final de la conversation ne peut donc pas
     * être relu depuis l'état React au moment du `done`. C'est ce cumul local qui sert aussi de
     * repli quand le serveur ne renvoie pas de contenu final.
     */
    let texte = '';
    let idConversation = null;

    const surEvenement = (nom, donnees) => {
      if (nom === 'start') {
        idConversation = donnees.conversationId;
        setMessages([{
          id: 'initial',
          role: 'USER',
          content: donnees.question,
          createdAt: new Date().toISOString()
        }]);
        setStreamingPhase('Analyse en cours…');
      } else if (nom === 'chunk') {
        texte += donnees.text;
        setStreamingContent(prev => prev + donnees.text);
      } else if (nom === 'reset') {
        /*
         * Le serveur va interroger la régie : ce qui précédait n'était que l'amorce d'une
         * réponse qu'il reprend. Sans cette remise à zéro, les deux textes se collaient —
         * « Laisse-moi interroger la régie.Aucune donnée de produits… ».
         */
        texte = '';
        setStreamingContent('');
        setStreamingPhase(donnees.raison || 'Récupération des données…');
      } else if (nom === 'error') {
        throw new Error(donnees.message);
      } else if (nom === 'done') {
        const analyse = texte;
        setMessages(prev => [...prev, {
          id: 'initial-response',
          role: 'ASSISTANT',
          content: analyse,
          // Le graphique arrive avec le message final, comme pour les messages de suivi.
          artifacts: donnees.artifacts || null,
          tokensUsed: donnees.tokensUsed || 0,
          estimatedCost: donnees.estimatedCost || 0,
          createdAt: new Date().toISOString()
        }]);
        // La conversation existe déjà en base : la poser ici suffit à ce que la suite de
        // l'échange parte sur `/api/conversations/:id/messages`.
        setConversation({ id: idConversation || donnees.conversationId });
        if (onStreamComplete) onStreamComplete({ success: true, analysis: analyse });
      }
    };

    try {
      await lireFluxSse(`${API_URL}/api/analyze-dashboard/stream`, {
        token,
        corps: question ? { ...corps, userBrief: question } : corps,
        signal: controleur.signal,
        surEvenement,
        // Le serveur plafonne les analyses à cinq par minute, et son message dit quoi faire.
        refusParDefaut: "Le serveur a refusé l'analyse"
      });
    } catch (erreur) {
      if (!estUnAbandon(erreur)) {
        console.error('[CHAT] Analyse du dashboard:', erreur);
        setError(erreur.message || 'Erreur lors de l\'analyse');
      }
    } finally {
      setIsStreaming(false);
      setStreamingContent('');
      setStreamingPhase('');
      abortRef.current = null;
    }
  };

  const startStreaming = () => {
    console.log('[CHAT] Démarrage streaming avec:', { libraryItemIds, initialQuery, accountId });

    if (!libraryItemIds || libraryItemIds.length === 0) {
      setError('Aucun élément de bibliothèque sélectionné');
      return;
    }

    setIsStreaming(true);
    setStreamingContent('');
    setStreamingPhase('Initialisation...');
    setError(null);

    // Ajouter le message utilisateur immédiatement
    setMessages([{
      id: 'initial',
      role: 'USER',
      content: initialQuery,
      createdAt: new Date().toISOString()
    }]);

    // Construire l'URL avec query params (EventSource ne supporte pas les headers)
    const params = new URLSearchParams({
      libraryItemIds: libraryItemIds.join(','),
      query: initialQuery,
      token: token
    });
    const streamUrl = `${API_URL}/api/analyze/library/stream?${params.toString()}`;
    console.log('[CHAT] Stream URL:', streamUrl);

    const eventSource = new EventSource(streamUrl);
    eventSourceRef.current = eventSource;

    eventSource.addEventListener('status', (e) => {
      const data = JSON.parse(e.data);
      setStreamingPhase(data.message || data.phase);
    });

    eventSource.addEventListener('account_info', (e) => {
      // Info du compte reçue - on peut l'utiliser pour afficher le nom
      const data = JSON.parse(e.data);
      console.log('[CHAT] Account info:', data);
    });

    eventSource.addEventListener('data_fetched', (e) => {
      const data = JSON.parse(e.data);
      setStreamingData(data);
      setStreamingPhase(`${data.rowCount} lignes récupérées...`);
    });

    eventSource.addEventListener('chunk', (e) => {
      const data = JSON.parse(e.data);
      setStreamingContent(prev => prev + data.text);
      setStreamingPhase('Analyse en cours...');
    });

    eventSource.addEventListener('complete', (e) => {
      const data = JSON.parse(e.data);
      setIsStreaming(false);
      eventSource.close();
      eventSourceRef.current = null;

      // Mettre à jour avec le message final complet
      setMessages(prev => [
        ...prev.filter(m => m.id === 'initial'),
        {
          id: 'initial-response',
          role: 'ASSISTANT',
          content: data.analysis || streamingContent,
          artifacts: data.artifacts || [],
          gaqlQuery: data.gaqlQuery,
          dataRowCount: streamingData?.rowCount || data.data?.length,
          usedExistingData: false,
          tokensUsed: data.tokensUsed || 0,
          createdAt: new Date().toISOString()
        }
      ]);

      // Notifier le parent que le streaming est terminé
      if (onStreamComplete) {
        onStreamComplete({
          success: true,
          analysis: data.analysis,
          artifacts: data.artifacts,
          data: data.data,
          gaqlQuery: data.gaqlQuery,
          tokensUsed: data.tokensUsed
        });
      }

      // Créer la conversation en base maintenant
      createConversationAfterStream(data);
    });

    eventSource.addEventListener('error', (e) => {
      let errorMsg = 'Erreur lors de l\'analyse';
      try {
        const data = JSON.parse(e.data);
        errorMsg = data.error || errorMsg;
      } catch {}
      setError(errorMsg);
      setIsStreaming(false);
      eventSource.close();
      eventSourceRef.current = null;
    });

    eventSource.onerror = () => {
      if (eventSource.readyState === EventSource.CLOSED) {
        // Normal close, ignore
      } else {
        setError('Connexion perdue avec le serveur');
        setIsStreaming(false);
      }
    };
  };

  /**
   * Créer la conversation après que le streaming est terminé
   */
  const createConversationAfterStream = async (streamResult) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/conversations`,
        {
          accountId,
          accountName,
          initialQuery,
          initialData: streamResult?.data?.slice(0, 100) || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setConversation(response.data.conversation);
      }
    } catch (err) {
      console.error('[CHAT] Erreur création conversation après stream:', err);
      // Non bloquant - l'utilisateur peut quand même voir les résultats
    }
  };

  const createConversation = async () => {
    setIsCreating(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/conversations`,
        {
          accountId,
          accountName,
          initialQuery,
          initialData: analysisResult?.data || analysisResult?.sampleData || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setConversation(response.data.conversation);

        // Ajouter le message initial (la question de l'analyse)
        setMessages([{
          id: 'initial',
          role: 'USER',
          content: initialQuery,
          createdAt: new Date().toISOString()
        }, {
          id: 'initial-response',
          role: 'ASSISTANT',
          content: analysisResult?.analysis || analysisResult?.claudeAnalysis || 'Analyse chargée.',
          artifacts: analysisResult?.artifacts,
          usedExistingData: true,
          createdAt: new Date().toISOString()
        }]);
      }
    } catch (err) {
      console.error('[CHAT] Erreur création conversation:', err);
      setError('Erreur lors de la création de la conversation');
    } finally {
      setIsCreating(false);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isStreaming) return;

    const userMessage = inputValue.trim();

    /*
     * La première question d'un panneau de dashboard ouvre la conversation.
     *
     * Il n'y a encore rien en base : c'est la route d'analyse qui crée la conversation, avec les
     * chiffres affichés en contexte. Les questions suivantes passent par `/messages`, qui les
     * relit depuis `initialData`. Sans ce branchement, le garde `!conversation` renvoyait sans
     * rien faire et le champ de saisie restait inerte.
     */
    if (dashboardStream && !conversation) {
      setInputValue('');
      setError(null);
      await startDashboardStream(userMessage);
      return;
    }

    if (!conversation) return;

    setInputValue('');
    setError(null);

    // Ajouter le message utilisateur immédiatement (optimistic UI)
    const tempUserMessage = {
      id: `temp-${Date.now()}`,
      role: 'USER',
      content: userMessage,
      createdAt: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMessage]);

    /*
     * La réponse s'écrit, elle n'apparaît pas.
     *
     * Les questions de suivi passaient par la route JSON et s'affichaient d'un bloc après dix à
     * quarante secondes d'attente muette, alors que la première question se déroulait mot à mot.
     * Le contraste se voyait, et l'attente paraissait plus longue qu'elle ne l'est.
     */
    setIsStreaming(true);
    setStreamingContent('');
    setStreamingPhase('Réflexion…');
    const controleur = new AbortController();
    abortRef.current = controleur;

    try {
      const reponse = await fetch(`${API_URL}/api/conversations/${conversation.id}/messages/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: userMessage }),
        signal: controleur.signal
      });

      if (!reponse.ok || !reponse.body) {
        const motif = await reponse.json().catch(() => null);
        throw new Error(motif?.message || motif?.error || `Le serveur a refusé le message (${reponse.status})`);
      }

      const lecteur = reponse.body.getReader();
      const decodeur = new TextDecoder();
      let tampon = '';

      while (true) {
        const { done, value } = await lecteur.read();
        if (done) break;

        tampon += decodeur.decode(value, { stream: true });
        const blocs = tampon.split('\n\n');
        tampon = blocs.pop() || '';

        for (const bloc of blocs) {
          const nom = bloc.match(/^event: (\w+)/m)?.[1];
          const brut = bloc.match(/^data: (.*)$/m)?.[1];
          if (!nom || !brut) continue;
          const donnees = JSON.parse(brut);

          if (nom === 'start') {
            // Le message définitif remplace l'optimiste, qui n'avait qu'un identifiant provisoire.
            setMessages(prev => prev.map(m => (m.id === tempUserMessage.id ? donnees.userMessage : m)));
            setStreamingPhase('Analyse en cours…');
          } else if (nom === 'chunk') {
            setStreamingContent(prev => prev + donnees.text);
          } else if (nom === 'reset') {
            /*
             * Le serveur va interroger Google Ads : ce qui précédait n'était que l'amorce d'une
             * réponse qu'il reprend. On affiche la raison plutôt qu'un libellé générique — une
             * attente de dix secondes sans explication se lit comme une panne.
             */
            setStreamingContent('');
            setStreamingPhase(donnees.raison || 'Récupération des données…');
          } else if (nom === 'error') {
            throw new Error(donnees.message);
          } else if (nom === 'done') {
            setMessages(prev => [...prev, donnees.assistantMessage]);
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[CHAT] Erreur envoi message:', err);
        setError(err.message || 'Erreur lors de l\'envoi du message');
        setMessages(prev => prev.filter(m => m.id !== tempUserMessage.id));
      }
    } finally {
      setIsStreaming(false);
      setStreamingContent('');
      setStreamingPhase('');
      abortRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleClose = () => {
    onClose();
    // Optionnel: reset la conversation si on veut repartir de zéro à chaque ouverture
    // setConversation(null);
    // setMessages([]);
  };

  // Utiliser un Portal pour rendre en dehors de la hiérarchie DOM
  // Cela évite les problèmes de z-index avec les stacking contexts parents
  return ReactDOM.createPortal(
    <>
      {/* Overlay */}
      <div
        className={`chat-panel-overlay ${isOpen && !sansOverlay ? 'visible' : ''}`}
        onClick={handleClose}
      />

      {/* Panel */}
      <div className={`chat-panel ${isOpen ? 'open' : ''} ${sansOverlay ? 'chat-panel--cote' : ''}`}>
        {/* La poignée n'a de sens qu'à côté du contenu : un panneau qui recouvre la page n'a pas
            de largeur à négocier avec elle. */}
        {sansOverlay && (
          <div
            className="chat-panel-poignee"
            onPointerDown={commencerRedimensionnement}
            role="separator"
            aria-orientation="vertical"
            aria-label="Redimensionner le panneau"
            title="Glisser pour redimensionner"
          />
        )}
        {/* Header */}
        <div className="chat-panel-header">
          <div className="chat-panel-title">
            <MessageSquare size={18} />
            <span>{conversation?.title || 'Conversation'}</span>
          </div>
          <button onClick={handleClose} className="chat-panel-close">
            <X size={18} />
          </button>
        </div>

        {/* Messages */}
        <div className="chat-panel-messages">
          {isCreating ? (
            <div className="chat-loading-state">
              <Loader2 size={24} className="spin" />
              <span>Initialisation de la conversation...</span>
            </div>
          ) : messages.length === 0 && !isStreaming ? (
            <div className="chat-empty-state">
              <Sparkles size={32} />
              <p>
                {dashboardStream
                  ? 'Posez une question sur les résultats affichés.'
                  : 'Posez une question de suivi pour continuer l\'analyse.'}
              </p>
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <ChatMessage key={msg.id} message={msg} />
              ))}

              {/* Affichage du streaming en cours */}
              {isStreaming && (
                <div className="chat-message assistant streaming">
                  {/* Pas d'avatar ici non plus : la ligne « Analyse en cours… » juste en
                      dessous, avec son disque qui tourne, dit déjà ce qu'il se passe. */}
                  <div className="chat-message-content">
                    {/* Phase de streaming */}
                    <div className="streaming-phase">
                      <Loader2 size={12} className="spin" />
                      <span>{streamingPhase}</span>
                    </div>
                    {/* Contenu en cours de streaming */}
                    {streamingContent && (
                      /*
                       * Le texte en cours passe par le **même** formateur que le message final.
                       *
                       * Il s'affichait brut : on lisait `**Ordinateur**`, `###` et les tirets de
                       * liste défiler à l'écran, puis tout se remettait en forme d'un coup à la
                       * fin. Le curseur reste hors du HTML formaté, pour ne pas se retrouver
                       * enfermé dans le dernier bloc ouvert.
                       */
                      <div className="chat-message-text streaming-text">
                        <span dangerouslySetInnerHTML={{ __html: formatMessage(streamingContent) }} />
                        <span className="streaming-cursor">▋</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="chat-panel-error">
            {error}
          </div>
        )}

        {/* Input */}
        <div className="chat-panel-input">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isStreaming
              ? 'Analyse en cours...'
              : (dashboardStream && !conversation
                ? 'Posez une question sur ce dashboard...'
                : 'Posez une question de suivi...')}
            /* Le champ reste ouvert avant toute conversation quand le panneau analyse un
               dashboard : c'est la première question qui la crée. */
            disabled={isCreating || isStreaming || (!conversation && !dashboardStream)}
            rows={1}
          />
          <button
            onClick={sendMessage}
            disabled={!inputValue.trim() || isCreating || isStreaming || (!conversation && !dashboardStream)}
            className="chat-send-btn"
          >
            {isStreaming ? (
              <Loader2 size={18} className="spin" />
            ) : (
              <Send size={18} />
            )}
          </button>
        </div>

        {/* Footer info */}
        {(conversation || isStreaming || accountId) && (
          <div className="chat-panel-footer">
            <Database size={12} />
            <span>Compte: {accountName || accountId}</span>
          </div>
        )}
      </div>
    </>,
    document.body
  );
};

export default ChatPanel;
